import { usePreview } from '../../shares/views/PreviewView/PreviewContext'
import { useVersioning } from '../../versioning'
import { useGetPresentationAssetsByArtboardQuery } from '@sketch/gql-types'

interface UsePresentationFileProps {
  artboardUuid: string
}

/**
 * When we arrive on a prototype we just have the start Artboard UUID. However
 * presentation file assets for the whole doc are currently accessed via the
 * Page type. So we have to go via the Artboard's page to get the presentation
 * file info.
 */
export const usePresentationFile = ({
  artboardUuid,
}: UsePresentationFileProps) => {
  const context = useVersioning()
  const previewContext = usePreview()

  const documentId = context.loading
    ? null
    : (context?.currentVersion.document?.identifier ?? null)

  const prFileQuery = useGetPresentationAssetsByArtboardQuery({
    variables: {
      artboardUuid: artboardUuid,
      documentId: documentId ?? '',
    },
    skip: !context || context.loading,
  })

  // in previews, we have to massage this a bit
  if (previewContext) {
    const prototypes = previewContext.prototypes || []

    const matchedPrototype = prototypes.find(
      prototype =>
        prototype.__typename === 'Frame' && prototype.uuid === artboardUuid
    )
    const page =
      matchedPrototype?.__typename === 'Frame' ? matchedPrototype?.page : null

    return {
      page,
      isLoading: false,
      hasError: false,
      hasNotFoundError: false,
      isRendering: false,
    }
  }

  const isRendering = context?.renderStatus !== 'ready'
  const page = prFileQuery.data?.artboardInDocumentWithUuid?.page
  const isLoading = prFileQuery.loading || context.loading
  const hasError = prFileQuery.error
  const hasNotFoundError = prFileQuery.error?.graphQLErrors.some(
    x => x.extensions?.code === 'NOT_FOUND'
  )

  return {
    page,
    isLoading,
    hasError,
    hasNotFoundError,
    isRendering,
  }
}
